import { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { Image } from '@storybook';
import { Drawer, DrawerHead, Kebab, StatusChip } from 'views/design-system/components';

/* What `POST /payment-methods` will accept. A Plaid subtype outside this set
   400s, so the control is hidden rather than offered and then failing. */
const ACCOUNT_TYPES = ['CHECKING', 'SAVINGS', 'BUSINESS'];

export type IDefaultSelection =
	| {kind: 'card'; id?: string}
	| {
			kind: 'ach';
			bankId?: string;
			accountId?: string;
			accountType?: string;
	  };

import {
	IBankMethod,
	ICardMethod,
	IWireMethod,
	MethodKind,
	cardBrandImage,
	isCardExpired,
} from './derive';

/**
 * PAYMENT METHODS — the management surface (spec 18 §18.4).
 *
 * The rail shows what will be charged; this shows everything on file and what
 * can be done to it. A drawer rather than a modal because the page behind it is
 * the context — you are usually here because the ledger or the alert band told
 * you something was wrong with the card.
 *
 * THERE IS NO "EDIT CARD", AND THAT IS NOT AN OMISSION. There is no
 * card-update endpoint anywhere in this codebase — `addPaymentCard` only POSTs
 * `/business-cards {token, default}`. A card number cannot be changed in place
 * by us or by Stripe; you add a replacement and make it default. Offering an
 * "Edit" that could only ever change nothing would be a lie in a form.
 *
 * Group order is FIXED — Cards, Bank accounts, Wire. Stable geography means a
 * returning user knows where to look; the DEFAULT chip does the finding rather
 * than reordering the list under them.
 */
export const PaymentDrawer: FC<{
	open: boolean;
	onClose: () => void;
	cards: ICardMethod[];
	banks: IBankMethod[];
	wire?: IWireMethod | null;
	defaultKind: MethodKind;
	defaultCardId?: string;
	/* Which bank/account the ACH designation names — both are needed or every
	   bank row chips as default. */
	defaultBankId?: string | null;
	defaultAccountId?: string | null;
	/* True when the current default is an expired card: every other row's
	   button becomes primary, because promoting one is the fix. */
	defaultIsExpired?: boolean;
	/* The selection currently being saved, so its button can say so. */
	pendingDefault?: any;
	canManage: boolean;
	onAddCard: () => void;
	onLinkBank: () => void;
	/**
	 * The selection to make default — a specific instrument, not a rail.
	 * `{kind:'card', id}` or `{kind:'ach', bankId, accountId, accountType}`.
	 * Widened from `ICardMethod` when banks became designatable: a card-shaped
	 * parameter cannot express which ACCOUNT within a bank was chosen.
	 */
	onSetDefault: (selection: IDefaultSelection) => void;
	onRemove: (card: ICardMethod) => void;
	billingAddress: string | null;
	onEditAddress: ReactNode;
	/* The add / replace wizard (Card / ACH / Wire picker, then the Stripe, Plaid,
	   wire or address step) renders HERE, in place of the list, while `adding`.
	   It is the legacy step machine mounted whole; the deck owns the flag. */
	adding?: boolean;
	addForm?: ReactNode;
}> = ({
	open,
	onClose,
	cards,
	banks,
	wire,
	defaultKind,
	defaultCardId,
	defaultBankId,
	defaultAccountId,
	defaultIsExpired,
	pendingDefault,
	canManage,
	onAddCard,
	onLinkBank,
	onSetDefault,
	onRemove,
	billingAddress,
	onEditAddress,
	adding = false,
	addForm = null,
}) => {
	const [copied, setCopied] = useState(false);

	const bankAccountCount = useMemo(
		() => banks.reduce((n, b) => n + (b.accounts?.length ?? 0), 0),
		[banks]
	);

	/* Removing the default while other methods exist would leave nothing
	   designated to charge. The rule is stated where the action is rather than
	   left for the server to refuse. */
	const otherMethods = cards.length + bankAccountCount;
	const defaultBlocksRemoval = otherMethods > 1;

	const copyWire = useCallback(() => {
		if (!wire) return;
		const text = [
			wire.bankName,
			`Account: ${wire.accountNumber}`,
			`Routing: ${wire.routingNumber}`,
		]
			.filter(Boolean)
			.join('\n');
		navigator.clipboard?.writeText(text).then(
			() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2500);
			},
			() => undefined
		);
	}, [wire]);

	const cardItems = (c: ICardMethod) => {
		const isDefault = defaultKind === 'card' && c.id === defaultCardId;
		const expired = isCardExpired(c);
		return [
			/* The row now carries a visible "Make default" button, so the kebab
			   duplicate is gone — one affordance per action. Expired stays
			   excluded here for the same reason it always was: promoting a card
			   that cannot be charged is not a fix. */
			...(isDefault || expired
				? []
				: [
						{
							label: 'Set as default',
							onClick: () => onSetDefault({ kind: 'card' as const, id: c.id }),
						},
				  ]),
			{ label: 'Replace card…', onClick: onAddCard },
			{
				label: 'Remove…',
				onClick: () => onRemove(c),
				disabled: isDefault && defaultBlocksRemoval,
				hint:
					isDefault && defaultBlocksRemoval
						? 'Set another method as default first'
						: undefined,
			},
		];
	};

	return (
		<Drawer open={open} label="Payment methods">
			<DrawerHead
				title={adding ? 'Add a payment method' : 'Payment methods'}
				onClose={onClose}
			/>

			{/* ONE SURFACE, TWO VIEWS. While adding, the form REPLACES the list rather
			    than stacking under it: a picker, a card form and three groups of rows
			    in one 480px column is a scroll with no focus, and the wizard's own
			    Back already returns here. */}
			{adding ? (
				<div className="pay-drawer" id="billing-add-payment">
					{addForm}
				</div>
			) : (
			<div className="pay-drawer">
				{/* ── cards ── */}
				<div className="dsec">
					<div className="sh">
						Cards
						{canManage && (
							<button type="button" className="dbtn" onClick={onAddCard}>
								+ Add card
							</button>
						)}
					</div>

					{cards.length ? (
						<>
							{cards.map(c => {
								const isDefault = defaultKind === 'card' && c.id === defaultCardId;
								const expired = isCardExpired(c);
								return (
									<div
										key={c.id}
										className={`pmrow${expired ? ' pmrow--bad' : ''}`}
									>
										<Image
											fileName={cardBrandImage(c.brand)}
											className="pmrow__brand"
										/>
										<div className="pmrow__main">
											<div className="pmrow__t">
												{(c.brand ?? 'Card').toUpperCase()}
												<span className="pmrow__num">···· {c.last4}</span>
											</div>
											<div className="pmrow__sub">
												{expired ? 'expired' : 'exp'}{' '}
												{String(c.exp_month).padStart(2, '0')}/
												{String(c.exp_year).slice(-2)}
											</div>
										</div>
										{/* A FIXED SLOT: chip when this row IS the default, button
										    when it can become one. Chip = state, button = action,
										    one place to look.

										    Expired AND default shows BOTH chips — the old code hid
										    Default behind Expired, which is exactly backwards: an
										    expired card that is still the default is the failure
										    worth shouting about, because every charge will bounce. */}
										<span className="pmrow__slot">
											{expired && <StatusChip verdict="rej">Expired</StatusChip>}
											{isDefault ? (
												<StatusChip verdict="ns">Default</StatusChip>
											) : (
												canManage && (
													<button
														type="button"
														className={`dbtn${defaultIsExpired ? ' pri' : ''}`}
														disabled={!!pendingDefault}
														onClick={() => onSetDefault({kind: 'card', id: c.id})}
													>
														{pendingDefault?.id === c.id ? 'Saving…' : 'Make default'}
													</button>
												)
											)}
										</span>
										{canManage && <Kebab items={cardItems(c)} />}
									</div>
								);
							})}
							{/*
							  * AN EXPIRED CARD THAT IS STILL THE DEFAULT IS A LIVE FAILURE.
							  *
							  * Every charge against it will bounce — the monthly run included —
							  * and nothing else on this screen says so: the row shows an
							  * "Expired" chip, but a chip is a label, not a warning, and it
							  * does not tell anyone what to do about it. This names the
							  * consequence and both fixes, and it sits under the group rather
							  * than on the row because the remedy (promote another method) is
							  * a decision about the SET, not about that card.
							  *
							  * `isCardExpired` already existed and nothing acted on it.
							  */}
							{defaultIsExpired && (
								<div className="pm-note pm-note--warn">
									<b>Charges will fail.</b> The default card has expired — make
									another method the default, or replace this card.
								</div>
							)}
							{canManage && defaultBlocksRemoval && (
								<div className="pm-note">
									The default method can&apos;t be removed — set another method as
									default first.
								</div>
							)}
						</>
					) : (
						<div className="pm-well">
							<div className="t">No cards on file</div>
							<div className="d">A card is charged automatically each cycle.</div>
							{canManage && (
								<button type="button" className="dbtn" onClick={onAddCard}>
									Add card
								</button>
							)}
						</div>
					)}
				</div>

				{/* ── bank accounts ── */}
				<div className="dsec">
					<div className="sh">
						Bank accounts
						{canManage && (
							<button type="button" className="dbtn" onClick={onLinkBank}>
								+ Link bank
							</button>
						)}
					</div>

					{bankAccountCount ? (
						banks.map(b =>
							(b.accounts ?? []).map(a => (
								<div key={a.id ?? a.mask} className="pmrow">
									<div className="pmrow__main">
										<div className="pmrow__t">
											{(b.bankName ?? 'Bank').toUpperCase()}
											<span className="pmrow__num">···· {a.mask}</span>
										</div>
										<div className="pmrow__sub">{a.name ?? 'Bank account'}</div>
									</div>
									{/* Matched on BOTH bank and account: `defaultKind === 'ach'`
									    alone chipped every bank row, so an account with two
									    linked accounts showed two defaults. */}
									<span className="pmrow__slot">
										{defaultKind === 'ach' &&
										defaultBankId === b._id &&
										(!defaultAccountId || defaultAccountId === a.accountId) ? (
											<StatusChip verdict="ns">Default</StatusChip>
										) : (
											canManage &&
											ACCOUNT_TYPES.includes(String(a.subtype ?? '').toUpperCase()) && (
												<button
													type="button"
													className="dbtn"
													disabled={!!pendingDefault}
													onClick={() =>
														onSetDefault({
															kind: 'ach',
															bankId: b._id,
															accountId: a.accountId,
															accountType: a.subtype,
														})
													}
												>
													{pendingDefault?.accountId === a.accountId ? 'Saving…' : 'Make default'}
												</button>
											)
										)}
									</span>
								</div>
							))
						)
					) : (
						<div className="pm-well">
							<div className="t">No linked bank accounts</div>
							<div className="d">Link one to pay by ACH.</div>
							{canManage && (
								<button type="button" className="dbtn" onClick={onLinkBank}>
									Link bank
								</button>
							)}
						</div>
					)}
				</div>

				{/* ── billing address ──
				    It is ACCOUNT-level, not per-card — one address however many
				    cards — which is why the spec first kept it out of here. But
				    nobody replaces a card and then goes hunting for a separate
				    control to fix the address that goes with it; the two are one
				    errand. It lives here AND stays visible in the rail, because
				    what is on file is worth reading without opening anything. */}
				<div className="dsec">
					<div className="sh">
						Billing address
						{canManage && <span className="pm-addr__act">{onEditAddress}</span>}
					</div>
					<div className="pmrow pmrow--addr">
						<div className="pmrow__main">
							{billingAddress ? (
								<div className="pmrow__sub">{billingAddress}</div>
							) : (
								<div className="pmrow__sub">
									None saved — it appears on every invoice.
								</div>
							)}
						</div>
					</div>
				</div>

				{/* ── wire ──
				    SHOWN IN FULL, DELIBERATELY. These are Simplici's RECEIVING
				    details — where the customer sends money. Masking them would
				    make the instructions useless. The never-render-in-full rule
				    is about card numbers, which are a credential; a destination
				    account is the opposite. */}
				{wire?.accountNumber && (
					<div className="dsec">
						<div className="sh">Wire transfer</div>
						<div className="pmrow pmrow--wire">
							<div className="pmrow__main">
								<div className="pmrow__t">{wire.bankName ?? 'Bank'}</div>
								<div className="pmrow__sub">
									acct {wire.accountNumber} · routing {wire.routingNumber}
								</div>
							</div>
							<button type="button" className="dbtn" onClick={copyWire}>
								{copied ? 'Copied ✓' : 'Copy instructions'}
							</button>
							{defaultKind === 'wire' && (
								<StatusChip verdict="ns">Default</StatusChip>
							)}
						</div>
					</div>
				)}
			</div>
			)}
		</Drawer>
	);
};
